import { useEffect, useState } from 'react'
import { OrderDetailsV3Query } from '~/__generated__/app/query/order-details/order-details.graphql'
import { sendKinesisLog } from '~/app/utils/kinesis'
import { DateTime } from 'luxon'
import {
  busMetaFromOrderDetailTickets,
  trainMetaFromOrderCompleteTickets,
} from '~/app/kinesis/kinesis-helper'

import { getLeisureProductCategoryForRequest } from '~/api/converters/domestic/order/ClientLogs/kinesis'

type DomesticAccommodationOrderDetail = Extract<
  OrderDetailsV3Query['orderDetailsV3']['categoryOrders'][number],
  { __typename?: 'OrderDetailsDomestic' }
>
type DomesticLeisureOrderDetail = Extract<
  OrderDetailsV3Query['orderDetailsV3']['categoryOrders'][number],
  { __typename?: 'OrderDetailsLeisureProduct' }
>
type DomesticTrainOrderDetail = Extract<
  OrderDetailsV3Query['orderDetailsV3']['categoryOrders'][number],
  { __typename?: 'OrderDetailsTrain' }
>

type DomesticBusOrderDetail = Extract<
  OrderDetailsV3Query['orderDetailsV3']['categoryOrders'][number],
  { __typename?: 'OrderDetailsBus' }
>

const orderDomesticAccommodation = (order: DomesticAccommodationOrderDetail) => {
  return {
    propertyId: order.propertyId,
    roomTypeId: order.roomTypeId,
    useType: order.useType,
    price: String(order.price),
    status: order.statusCode,
    couponId: order.discount?.coupons[0]?.id || 0,
    couponAmount: String(order.discount?.coupons[0]?.amount || 0),
    membershipProvider: order.discount?.mobileMembership?.type || '',
    membershipAmount: String(order.discount?.mobileMembership?.amount || 0),
    checkInDate: DateTime.fromISO(order.checkInAt).toFormat('yyyy-MM-dd'),
    checkInTime: DateTime.fromISO(order.checkInAt).toFormat('HH:mm'),
    ratePlanId: order.ratePlanId,
    badgeMemberCode: order.memberClass,
  }
}

const orderDomesticLeisure = (order: DomesticLeisureOrderDetail) => {
  return {
    items: order.items.map(item => ({
      dealId: item.dealId,
      optionId: item.optionId,
      optionName: item.optionName,
      price: item.price,
      status: item.status,
      purchaseOptionTypeCode: item.product.purchaseOptionTypeCode,
      productOptionGroupName: item.variant.productOptionGroup.productOptionGroupName || '',
      productOptionGroupId: item.variant.productOptionGroup.productOptionGroupId,
    })),
    couponId: order.discount?.coupons[0]?.id || 0,
    couponAmount: String(order.discount?.coupons[0]?.amount || 0),
    productType: getLeisureProductCategoryForRequest(order.items[0].product.productTypeCode),
  }
}

const orderDomesticTrain = (order: DomesticTrainOrderDetail) => {
  return {
    trainMeta: trainMetaFromOrderCompleteTickets(order),
    couponId: order.discount?.coupons[0]?.id || 0,
    couponAmount: String(order.discount?.coupons[0]?.amount || 0),
  }
}

const orderDomesticBus = (order: DomesticBusOrderDetail) => {
  return {
    busMeta: busMetaFromOrderDetailTickets(order),
    couponId: order.discount?.coupons[0]?.id || 0,
    couponAmount: String(order.discount?.coupons[0]?.amount || 0),
  }
}

export const orderProductOptions = (
  orders: OrderDetailsV3Query['orderDetailsV3']['categoryOrders']
) => {
  return orders.map(order => {
    return order.__typename === 'OrderDetailsDomestic'
      ? orderDomesticAccommodation(order)
      : order.__typename === 'OrderDetailsLeisureProduct'
        ? orderDomesticLeisure(order)
        : order.__typename === 'OrderDetailsTrain'
          ? orderDomesticTrain(order)
          : order.__typename === 'OrderDetailsBus'
            ? orderDomesticBus(order)
            : null
  })
}

const sendCartOrderDetail = (data: OrderDetailsV3Query['orderDetailsV3']) => {
  for (const categoryOrder of data?.categoryOrders || []) {
    // 하위호환 로그
    if (categoryOrder.__typename === 'OrderDetailsLeisureProduct') {
      sendKinesisLog({
        pageName: 'LeisureOrderDetail',
        eventType: 'view',
        leisureTicketNo: +categoryOrder.items[0].dealId,
      })
    } else if (categoryOrder.__typename === 'OrderDetailsTrain') {
      sendKinesisLog({
        pageName: 'TrainOrderDetail',
        eventType: 'view',
        ...orderDomesticTrain(categoryOrder),
      })
    }
  }
  sendKinesisLog({
    pageName: 'CartOrderDetail',
    eventType: 'view',
    hashOrderId: data.hashOrderId,
    productOptions: orderProductOptions(data.categoryOrders),
  })
}

export const useOrderDetailView = (data?: OrderDetailsV3Query['orderDetailsV3']) => {
  const [sent, setSent] = useState(false)
  useEffect(() => {
    if (data && data.categoryOrders.length > 0 && !sent) {
      sendCartOrderDetail(data)
      setSent(true)
    }
  }, [data, sent])
}

export const sendKinesisShareClick = (
  shareMethod: string,
  hashOrderId: string,
  isOrderComplete?: boolean
) => {
  if (isOrderComplete) {
    // 하위호환 로그
    sendKinesisLog({
      pageName: 'OrderComplete',
      eventType: 'click',
      eventLabel: 'shareOrder',
      object: 'shareBtn',
      shareChannel: shareMethod,
    })
  }

  sendKinesisLog({
    pageName: 'CartOrderDetail',
    eventType: 'click',
    object: shareMethod,
    objectType: 'shareMethod',
    hashOrderId,
  })
}

export const sendKinesisReceiptClick = (hashOrderId: string) => {
  sendKinesisLog({
    pageName: 'CartOrderDetail',
    eventType: 'click',
    object: 'receiptBtn',
    hashOrderId,
  })
}

export const sendKinesisOrderDeleteClick = (hashOrderId: string) => {
  sendKinesisLog({
    pageName: 'CartOrderDetail',
    eventType: 'click',
    object: 'orderDeleteBtn',
    hashOrderId,
  })
}

export const sendKinesisOrderCancelClick = (data?: OrderDetailsV3Query['orderDetailsV3']) => {
  for (const categoryOrder of data?.categoryOrders || []) {
    if (categoryOrder.__typename === 'OrderDetailsTrain') {
      // 하위호환 로그: 기차를 주문한 케이스에만 로그를 남김
      sendKinesisLog({
        pageName: 'TrainOrderDetail',
        eventType: 'click',
        object: 'cancelBtn',
        trainMeta: trainMetaFromOrderCompleteTickets(categoryOrder),
      })
      break
    }
  }
  sendKinesisLog({
    pageName: 'CartOrderDetail',
    eventType: 'click',
    object: 'cancelBtn',
    hashOrderId: data?.hashOrderId || '',
  })
}

export type SendKinesisProductLinkClickParams = {
  pageName: 'CartOrderDetail' | 'CartOrderList'
  hashOrderId: string
  hashCategoryOrderId?: string
}

export const sendKinesisSelfCheckinClick = ({
  pageName,
  hashCategoryOrderId,
  hashOrderId,
}: SendKinesisProductLinkClickParams) => {
  sendKinesisLog({
    pageName,
    eventType: 'click',
    object: 'selfCheckinBtn',
    hashOrderId,
    hashCategoryOrderId,
  })
}

export const sendKinesisTrainTicketClick = ({
  pageName,
  hashCategoryOrderId,
  hashOrderId,
}: SendKinesisProductLinkClickParams) => {
  sendKinesisLog({
    pageName,
    eventType: 'click',
    object: 'trainTicketBtn',
    hashOrderId,
    hashCategoryOrderId,
  })
}

export const sendKinesisUsageClick = ({
  pageName,
  hashCategoryOrderId,
  hashOrderId,
}: SendKinesisProductLinkClickParams) => {
  sendKinesisLog({
    pageName,
    eventType: 'click',
    object: 'usageBtn',
    hashOrderId,
    hashCategoryOrderId,
  })
}

export const sendKinesisReviewClick = async ({
  pageName,
  hashCategoryOrderId,
  hashOrderId,
}: SendKinesisProductLinkClickParams) => {
  await sendKinesisLog({
    pageName,
    eventType: 'click',
    object: 'writeReviewBtn',
    hashOrderId,
    hashCategoryOrderId,
  })
}

export const sendKinesisBusTicketButtonImpr = ({
  pageName,
  hashCategoryOrderId,
  hashOrderId,
}: SendKinesisProductLinkClickParams) => {
  sendKinesisLog({
    pageName,
    eventType: 'impr',
    object: 'busTicketBtn',
    hashOrderId,
    hashCategoryOrderId,
  })
}

export const sendKinesisBusTicketClick = ({
  pageName,
  hashCategoryOrderId,
  hashOrderId,
}: SendKinesisProductLinkClickParams) => {
  sendKinesisLog({
    pageName,
    eventType: 'click',
    object: 'busTicketBtn',
    hashOrderId,
    hashCategoryOrderId,
  })
}

export const sendKinesisOrderCancelButtonClick = ({
  pageName,
  hashOrderId,
}: SendKinesisProductLinkClickParams) => {
  sendKinesisLog({
    pageName,
    eventType: 'click',
    object: 'cancelBtn',
    hashOrderId,
  })
}

export const sendKinesisProductLinkClick = async ({
  id,
  pageName,
  hashOrderId,
  hashCategoryOrderId,
}: SendKinesisProductLinkClickParams & { id: string }) => {
  const params: SendKinesisProductLinkClickParams = {
    pageName,
    hashOrderId,
    hashCategoryOrderId,
  }
  if (id === 'self-checkin') {
    sendKinesisSelfCheckinClick(params)
  } else if (id === 'check-ticket') {
    sendKinesisTrainTicketClick(params)
  } else if (id === 'information') {
    sendKinesisUsageClick(params)
  } else if (id === 'review') {
    await sendKinesisReviewClick(params)
  } else if (id === 'check-bus-tickets') {
    sendKinesisBusTicketClick(params)
  } else if (id === 'cancel') {
    sendKinesisOrderCancelButtonClick(params)
  }
}

export const sendKinesisProductLinkImpressed = ({
  id,
  pageName,
  hashOrderId,
  hashCategoryOrderId,
}: SendKinesisProductLinkClickParams & { id: string }) => {
  const params: SendKinesisProductLinkClickParams = {
    pageName,
    hashOrderId,
    hashCategoryOrderId,
  }
  if (id === 'check-bus-tickets') {
    sendKinesisBusTicketButtonImpr(params)
  }
}

export const sendKinesisCancelDetailClick = (cancelIndex: number, hashOrderId: string) => {
  sendKinesisLog({
    pageName: 'CartOrderDetail',
    eventType: 'click',
    object: cancelIndex,
    objectType: 'cancelRefundBtn',
    hashOrderId,
  })
}

export const sendKinesisLogOrderDetailQrCodeSuccess = (qrCode: string, isSuccessYn: 'Y' | 'N') => {
  sendKinesisLog({
    pageName: 'CartOrderDetail',
    eventType: 'view',
    object: 'qrCodeSuccessYn',
    isSuccessYn,
    qrCode,
  })
}
